/* iter129 — Fatura Merkezi (Invoice Center) — INVOICE-READ-ONLY multi-provider.
 * Provider integration surface is invoice-only. NO payment/cheque/bank/kasa
 * data is fetched from providers. All ERP payments must be entered via the
 * existing Borç/Alacak → Ödeme Ekle flow (iter127). */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, Btn, Modal, Input, Select, Textarea } from "@/components/UI";
import { Plus, RefreshCw, Trash2, Link as LinkIcon, ExternalLink, CheckCircle2, AlertTriangle, Search, FileText, Building2, Users, Wallet } from "lucide-react";
import { todayTR } from "@/lib/date";

const money = (n, cur = "TRY") => `${(Number(n || 0)).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur === "TRY" ? "TL" : cur}`;

export default function FaturaMerkezi() {
  const [tab, setTab] = useState("pool");
  const tabs = [
    { k: "pool", l: "Fatura Havuzu", Icon: FileText },
    { k: "imported", l: "Sisteme Eklenenler", Icon: CheckCircle2 },
    { k: "unmatched", l: "Eşleştirme Gerekenler", Icon: AlertTriangle },
    { k: "manual", l: "Manuel Fatura", Icon: Plus },
    { k: "providers", l: "Provider Ayarları", Icon: Building2 },
  ];
  return (
    <div className="space-y-3" data-testid="fatura-merkezi-panel">
      <div className="flex gap-1 border-b border-slate-200 flex-wrap">
        {tabs.map(({ k, l, Icon }) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`fm-tab-${k}`}
            className={`px-3 py-1.5 text-xs font-semibold flex items-center gap-1 border-b-2 -mb-px ${tab === k ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-600 hover:text-slate-800"}`}>
            <Icon size={12} /> {l}
          </button>
        ))}
      </div>
      {tab === "pool" && <PoolTab onlyUnmatched={false} />}
      {tab === "imported" && <ImportedTab />}
      {tab === "unmatched" && <PoolTab onlyUnmatched={true} />}
      {tab === "manual" && <ManualInvoiceTab />}
      {tab === "providers" && <ProviderSettingsTab />}
    </div>
  );
}

function PoolTab({ onlyUnmatched }) {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [filters, setFilters] = useState({ type: "", provider: "", search: "" });
  const [msg, setMsg] = useState("");
  const [partyRow, setPartyRow] = useState(null); // triggers create+match modal
  const load = useCallback(async () => {
    const q = new URLSearchParams();
    if (filters.type) q.set("type", filters.type);
    if (filters.provider) q.set("provider", filters.provider);
    if (filters.search) q.set("search", filters.search);
    if (onlyUnmatched) q.set("matching", "unmatched");
    const r = await api.get(`/invoices/pool?${q.toString()}`);
    setRows(r?.rows || []);
    setSelected(new Set());
  }, [filters, onlyUnmatched]);
  useEffect(() => { load(); }, [load]);

  const toggle = id => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map(r => r.id)));
  };
  const importSel = async () => {
    if (selected.size === 0) { setMsg("Fatura seçin"); return; }
    try {
      const r = await api.post("/invoices/import", { invoice_ids: [...selected] });
      const failed = r.results.filter(x => !x.ok);
      setMsg(`${r.imported_count} fatura eklendi${failed.length ? `, ${failed.length} hata` : ""}`);
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Hata"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2 bg-slate-50 border border-slate-200 rounded p-2 text-xs">
        <Select label="Tür" value={filters.type} onChange={e => setFilters({ ...filters, type: e.target.value })} data-testid="fm-filter-type">
          <option value="">Tümü</option>
          <option value="SALES">SATIŞ</option>
          <option value="PURCHASE">ALIŞ</option>
        </Select>
        <Select label="Provider" value={filters.provider} onChange={e => setFilters({ ...filters, provider: e.target.value })} data-testid="fm-filter-provider">
          <option value="">Tümü</option>
          <option value="MANUEL">MANUEL</option>
          <option value="PARASUT">Paraşüt</option>
          <option value="BIRFATURA">BirFatura</option>
          <option value="PASTEL_BULUT">Pastel Bulut</option>
        </Select>
        <Input label="Ara" value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} placeholder="Fatura no / firma" data-testid="fm-filter-search"/>
        <Btn onClick={load} data-testid="fm-refresh"><Search size={12}/> Filtrele</Btn>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-slate-500">{selected.size} seçili</span>
          <Btn onClick={importSel} disabled={selected.size === 0} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="fm-import-selected">
            <Plus size={12}/> Seçilenleri Sisteme Ekle
          </Btn>
        </div>
      </div>
      {msg && <div className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 rounded p-2" data-testid="fm-pool-msg">{msg}</div>}
      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 text-slate-600 uppercase"><tr>
            <th className="px-2 py-2 w-6"><input type="checkbox" checked={rows.length > 0 && selected.size === rows.length} onChange={toggleAll} data-testid="fm-pool-select-all"/></th>
            <th className="text-left px-2 py-2">Provider</th>
            <th className="text-left px-2 py-2">Firma</th>
            <th className="text-left px-2 py-2">Tür</th>
            <th className="text-left px-2 py-2">Fatura No</th>
            <th className="text-left px-2 py-2">Tarih</th>
            <th className="text-left px-2 py-2">Vade</th>
            <th className="text-left px-2 py-2">Cari</th>
            <th className="text-left px-2 py-2">VKN</th>
            <th className="text-right px-2 py-2">Toplam</th>
            <th className="text-left px-2 py-2">Eşleşme</th>
            <th className="text-left px-2 py-2">İşlem</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={12} className="text-center py-6 text-slate-400">Havuzda fatura yok.</td></tr>}
            {rows.map(r => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`fm-pool-row-${r.id}`}>
                <td className="px-2 py-1.5"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} data-testid={`fm-select-${r.id}`}/></td>
                <td className="px-2 py-1.5"><span className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{r.provider}</span></td>
                <td className="px-2 py-1.5">{r.own_company || <span className="text-rose-600 text-[10px]">FİRMA EŞL. GEREKLİ</span>}</td>
                <td className="px-2 py-1.5"><span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${r.type === "SALES" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>{r.type === "SALES" ? "SATIŞ" : "ALIŞ"}</span></td>
                <td className="px-2 py-1.5 font-mono">{r.external_url ? <a href={r.external_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline flex items-center gap-1">{r.invoice_number} <ExternalLink size={10}/></a> : r.invoice_number}</td>
                <td className="px-2 py-1.5">{r.invoice_date}</td>
                <td className="px-2 py-1.5 text-amber-700">{r.due_date || "—"}</td>
                <td className="px-2 py-1.5">{r.party_name || "—"}</td>
                <td className="px-2 py-1.5 font-mono text-[10px]">{r.party_tax_id || "—"}</td>
                <td className="px-2 py-1.5 text-right font-mono font-semibold">{money(r.total, r.currency)}</td>
                <td className="px-2 py-1.5">
                  {r.matching_status === "matched"
                    ? <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">EŞLEŞTİ</span>
                    : <span className="text-[10px] bg-rose-100 text-rose-800 px-1.5 py-0.5 rounded">{r.type === "SALES" ? "MÜŞTERİ EŞL." : "TEDARİKÇİ EŞL."}</span>}
                </td>
                <td className="px-2 py-1.5">
                  {r.matching_status !== "matched" && (
                    <button onClick={() => setPartyRow(r)} className="text-[10px] px-2 py-1 rounded bg-indigo-100 hover:bg-indigo-200 text-indigo-800 border border-indigo-300 flex items-center gap-1" data-testid={`fm-create-party-${r.id}`}>
                      <Plus size={10}/> {r.type === "SALES" ? "Müşteri Ekle" : "Tedarikçi Ekle"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {partyRow && <CreatePartyModal row={partyRow} onClose={() => setPartyRow(null)} onSaved={() => { setPartyRow(null); load(); }} />}
    </div>
  );
}

function CreatePartyModal({ row, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: row.party_name || "",
    tax_id: row.party_tax_id || "",
    tax_office: "",
    phone: "",
    email: "",
    address: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    setErr("");
    if (!form.name.trim()) { setErr("Ad zorunlu"); return; }
    setBusy(true);
    try {
      await api.post(`/invoices/${row.id}/party/create`, form);
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Hata"); } finally { setBusy(false); }
  };
  const label = row.type === "SALES" ? "MÜŞTERİ EKLE" : "TEDARİKÇİ EKLE";
  return (
    <Modal open={true} onClose={onClose} title={label} size="md">
      <div className="space-y-3" data-testid="fm-create-party-modal">
        <div className="bg-indigo-50/60 border border-indigo-200 rounded p-2 text-[11px]">
          Fatura #{row.invoice_number} · {row.party_name || "—"} · VKN {row.party_tax_id || "—"}<br/>
          Yeni {row.type === "SALES" ? "müşteri" : "tedarikçi"} yalnızca CustomerERP'de oluşturulacak — harici providera hiçbir istek gönderilmez.
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input label="Firma Adı *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="fm-party-name"/>
          <Input label="VKN/TCKN" value={form.tax_id} onChange={e => setForm({ ...form, tax_id: e.target.value })} data-testid="fm-party-tax-id"/>
          <Input label="Vergi Dairesi" value={form.tax_office} onChange={e => setForm({ ...form, tax_office: e.target.value })}/>
          <Input label="Telefon" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}/>
          <Input label="E-posta" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}/>
          <Input label="Adres" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}/>
        </div>
        {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid="fm-party-err">{err}</div>}
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
          <Btn variant="ghost" onClick={onClose}>İptal</Btn>
          <Btn onClick={submit} disabled={busy} className="bg-emerald-600 text-white" data-testid="fm-party-save">
            {busy ? "Kaydediliyor..." : "Kaydet & Eşleştir"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

function ImportedTab() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [msg, setMsg] = useState("");
  const load = useCallback(async () => {
    const r = await api.get("/invoices/imported");
    setRows(r?.rows || []); setSelected(new Set());
  }, []);
  useEffect(() => { load(); }, [load]);
  const removeSel = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} fatura sistemden kaldırılacak. Harici provider'a hiçbir istek gönderilmez. Devam?`)) return;
    try {
      const r = await api.post("/invoices/remove", { invoice_ids: [...selected] });
      const failed = r.results.filter(x => !x.ok);
      setMsg(`${r.results.length - failed.length} kaldırıldı${failed.length ? `, ${failed.length} bloklandı (ödeme mevcut)` : ""}`);
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Hata"); }
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded p-2 text-xs">
        <span className="text-slate-500">{selected.size} seçili · Toplam {rows.length}</span>
        <Btn onClick={removeSel} disabled={selected.size === 0} className="bg-rose-600 text-white hover:bg-rose-700 ml-auto" data-testid="fm-remove-selected">
          <Trash2 size={12}/> Seçilenleri Sistemden Kaldır
        </Btn>
      </div>
      {msg && <div className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 rounded p-2" data-testid="fm-imported-msg">{msg}</div>}
      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 text-slate-600 uppercase"><tr>
            <th className="px-2 py-2 w-6"><input type="checkbox" checked={rows.length > 0 && selected.size === rows.length} onChange={() => selected.size === rows.length ? setSelected(new Set()) : setSelected(new Set(rows.map(r => r.id)))}/></th>
            <th className="text-left px-2 py-2">Provider</th>
            <th className="text-left px-2 py-2">Firma</th>
            <th className="text-left px-2 py-2">Tür</th>
            <th className="text-left px-2 py-2">Fatura No</th>
            <th className="text-left px-2 py-2">Cari</th>
            <th className="text-left px-2 py-2">Vade</th>
            <th className="text-right px-2 py-2">Toplam</th>
            <th className="text-right px-2 py-2">Kalan</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="text-center py-6 text-slate-400">Sisteme eklenmiş fatura yok.</td></tr>}
            {rows.map(r => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`fm-imp-row-${r.id}`}>
                <td className="px-2 py-1.5"><input type="checkbox" checked={selected.has(r.id)} onChange={() => { const n = new Set(selected); n.has(r.id) ? n.delete(r.id) : n.add(r.id); setSelected(n); }} data-testid={`fm-imp-select-${r.id}`}/></td>
                <td className="px-2 py-1.5">{r.provider}</td>
                <td className="px-2 py-1.5">{r.own_company || "—"}</td>
                <td className="px-2 py-1.5"><span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${r.type === "SALES" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>{r.type === "SALES" ? "SATIŞ" : "ALIŞ"}</span></td>
                <td className="px-2 py-1.5 font-mono">{r.invoice_number}</td>
                <td className="px-2 py-1.5">{r.party_name || "—"}</td>
                <td className="px-2 py-1.5 text-amber-700">{r.due_date || "—"}</td>
                <td className="px-2 py-1.5 text-right font-mono">{money(r.total, r.currency)}</td>
                <td className="px-2 py-1.5 text-right font-mono font-semibold text-rose-700">{money(r.remaining_amount ?? r.total, r.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-slate-500 italic">
        Kalan tutarlar ERP'nin kendi Borç/Alacak kayıtlarına göre hesaplanır. Provider'ın ödeme verileri hiçbir zaman içeri alınmaz.
      </div>
    </div>
  );
}

function ManualInvoiceTab() {
  const [form, setForm] = useState({
    own_company: "",
    type: "PURCHASE",
    invoice_number: "",
    invoice_date: todayTR(),
    due_date: "",
    party_id: "",
    party_name: "",
    party_tax_id: "",
    currency: "TRY",
    subtotal: "",
    vat: "",
    total: "",
    note: "",
  });
  const [parties, setParties] = useState([]);
  const [constants, setConstants] = useState({ own_companies: [] });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    (async () => {
      const c = await api.get("/invoices/constants");
      setConstants(c);
    })();
  }, []);
  useEffect(() => {
    (async () => {
      if (form.type === "PURCHASE") {
        const r = await api.get("/suppliers?active=true");
        setParties((r || []).map(s => ({ id: s.id, name: s.name })));
      } else {
        const r = await api.get("/customers?active=true");
        setParties((r || []).map(c => ({ id: c.id, name: c.legal_name })));
      }
    })();
  }, [form.type]);
  const submit = async () => {
    setMsg("");
    if (!form.invoice_number || !form.total) { setMsg("Fatura no ve toplam zorunlu"); return; }
    setBusy(true);
    try {
      const payload = { ...form, total: Number(form.total), subtotal: Number(form.subtotal || 0), vat: Number(form.vat || 0) };
      if (!payload.due_date) delete payload.due_date;
      if (!payload.party_id) delete payload.party_id;
      const r = await api.post("/invoices/manual", payload);
      setMsg(`Fatura oluşturuldu — Fatura Havuzu'nda görünecek (ID ${r.id.slice(0,8)}...)`);
      setForm({ ...form, invoice_number: "", total: "", subtotal: "", vat: "", note: "" });
    } catch (e) { setMsg(e?.response?.data?.detail || "Hata"); } finally { setBusy(false); }
  };
  return (
    <Card className="p-4" data-testid="fm-manual-form">
      <div className="text-sm font-bold text-slate-800 mb-3">Manuel Fatura Ekle</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Select label="Bizim Firmamız *" value={form.own_company} onChange={e => setForm({ ...form, own_company: e.target.value })} data-testid="fm-m-own-company">
          {(constants.own_companies || []).map(oc => <option key={oc} value={oc}>{oc}</option>)}
        </Select>
        <Select label="Tür *" value={form.type} onChange={e => setForm({ ...form, type: e.target.value, party_id: "" })} data-testid="fm-m-type">
          <option value="PURCHASE">ALIŞ</option>
          <option value="SALES">SATIŞ</option>
        </Select>
        <Select label={form.type === "PURCHASE" ? "Tedarikçi" : "Müşteri"} value={form.party_id} onChange={e => setForm({ ...form, party_id: e.target.value })} data-testid="fm-m-party">
          <option value="">— seçin —</option>
          {parties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Input label="Fatura No *" value={form.invoice_number} onChange={e => setForm({ ...form, invoice_number: e.target.value })} data-testid="fm-m-invno"/>
        <Input label="Fatura Tarihi *" type="date" value={form.invoice_date} onChange={e => setForm({ ...form, invoice_date: e.target.value })} data-testid="fm-m-date"/>
        <Input label="Vade Tarihi" type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} data-testid="fm-m-due"/>
        <Input label="Ara Toplam" type="number" step="0.01" value={form.subtotal} onChange={e => setForm({ ...form, subtotal: e.target.value })}/>
        <Input label="KDV" type="number" step="0.01" value={form.vat} onChange={e => setForm({ ...form, vat: e.target.value })}/>
        <Input label="Genel Toplam *" type="number" step="0.01" value={form.total} onChange={e => setForm({ ...form, total: e.target.value })} data-testid="fm-m-total"/>
      </div>
      <div className="mt-2">
        <Textarea label="Kısa Not" rows={2} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })}/>
      </div>
      {msg && <div className="mt-2 text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 rounded p-2" data-testid="fm-manual-msg">{msg}</div>}
      <div className="flex justify-end pt-2 mt-2 border-t border-slate-200">
        <Btn onClick={submit} disabled={busy} className="bg-emerald-600 text-white" data-testid="fm-manual-save">
          {busy ? "Kaydediliyor..." : "Faturayı Ekle"}
        </Btn>
      </div>
    </Card>
  );
}

function ProviderSettingsTab() {
  const [conns, setConns] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(async () => {
    const r = await api.get("/invoices/connections");
    setConns(r || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const refresh = async cid => {
    try {
      const r = await api.post(`/invoices/connections/${cid}/refresh`);
      setMsg(r.error ? `Hata: ${r.error}` : `Senkronize — ${r.new_sales} satış, ${r.new_purchase} alış eklendi`);
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Hata"); }
  };
  const testConn = async cid => {
    try {
      const r = await api.post(`/invoices/connections/${cid}/test`);
      const ch = r.checks || {};
      const d = r.diagnostics || {};
      const fmt = (probe) => {
        if (!probe || Object.keys(probe).length === 0) return "—";
        const st = probe.state || "?";
        const httpStr = probe.http_status ? ` HTTP ${probe.http_status}` : "";
        const ctStr = probe.content_type ? ` · ${probe.content_type}` : "";
        const cnt = probe.count != null ? ` · ${probe.count} kayıt` : "";
        if (st === "OK") return `✓ OK${httpStr}${cnt}`;
        return `✗ ${st}${httpStr}${ctStr} — ${probe.error || ""}`;
      };
      setMsg(
        `Test — ${r.ok ? "✓ BAŞARILI" : "✗ HATA"} · ` +
        `Auth: ${ch.authentication || "—"} · ` +
        `Gelen: ${d.list_incoming ? fmt(d.list_incoming) : (ch.list_incoming || "—")} · ` +
        `Giden: ${d.list_outgoing ? fmt(d.list_outgoing) : (ch.list_outgoing || "—")}` +
        (r.error ? ` · ${r.error}` : "")
      );
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Hata"); }
  };
  const del = async cid => {
    if (!window.confirm("Bağlantı silinsin? Harici provider'a hiçbir istek gönderilmez.")) return;
    await api.del(`/invoices/connections/${cid}`);
    load();
  };
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center bg-slate-50 border border-slate-200 rounded p-2">
        <div className="text-xs text-slate-600">Aynı anda birden çok provider bağlanabilir. Her provider yalnızca fatura okur — ödeme/çek/banka/kasa verisi çekilmez.</div>
        <Btn onClick={() => setShowCreate(true)} data-testid="fm-add-connection"><Plus size={12}/> Yeni Bağlantı</Btn>
      </div>
      {msg && <div className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 rounded p-2" data-testid="fm-provider-msg">{msg}</div>}
      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 text-slate-600 uppercase"><tr>
            <th className="text-left px-2 py-2">Provider</th>
            <th className="text-left px-2 py-2">Ad</th>
            <th className="text-left px-2 py-2">Durum</th>
            <th className="text-left px-2 py-2">Son Senkronizasyon</th>
            <th className="text-left px-2 py-2">İşlem</th>
          </tr></thead>
          <tbody>
            {conns.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-slate-400">Bağlantı yok.</td></tr>}
            {conns.map(c => (
              <tr key={c.id} className="border-t border-slate-100" data-testid={`fm-conn-${c.id}`}>
                <td className="px-2 py-1.5">{c.provider}</td>
                <td className="px-2 py-1.5">{c.name}</td>
                <td className="px-2 py-1.5"><span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${c.status === "connected" ? "bg-emerald-100 text-emerald-800" : c.status === "error" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>{c.status}</span></td>
                <td className="px-2 py-1.5 text-slate-500">{c.last_sync_at ? new Date(c.last_sync_at).toLocaleString("tr-TR") : "—"}</td>
                <td className="px-2 py-1.5 flex gap-1">
                  <button onClick={() => testConn(c.id)} className="text-[10px] px-2 py-1 rounded bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300 flex items-center gap-1" data-testid={`fm-conn-test-${c.id}`}>Test Et</button>
                  <button onClick={() => refresh(c.id)} className="text-[10px] px-2 py-1 rounded bg-indigo-100 hover:bg-indigo-200 text-indigo-800 border border-indigo-300 flex items-center gap-1" data-testid={`fm-conn-refresh-${c.id}`}><RefreshCw size={10}/> Senkronize</button>
                  <button onClick={() => del(c.id)} className="text-[10px] px-2 py-1 rounded bg-rose-100 hover:bg-rose-200 text-rose-800 border border-rose-300"><Trash2 size={10}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showCreate && <CreateConnectionModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function CreateConnectionModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    provider: "PARASUT", name: "", own_company: "",
    // Paraşüt
    client_id: "", client_secret: "", username: "", password: "", company_id: "",
    // BirFatura — iter135 default: Official OutEBelgeV2 (3 headers)
    auth_type: "birfatura_outebelgev2",
    api_key: "", secret_key: "", integration_key: "",
    // Legacy custom endpoint (behind Gelişmiş toggle)
    base_url: "", list_incoming_path: "", list_outgoing_path: "",
    auth_header_name: "X-API-Key", auth_token: "",
    own_tax_ids: "",
    mapping_json: "",
    // Sopyo/Pastel
    sopyo_api_key: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [constants, setConstants] = useState({ own_companies: [] });
  useEffect(() => {
    (async () => {
      try { const c = await api.get("/invoices/constants"); setConstants(c); } catch {}
    })();
  }, []);
  const submit = async () => {
    setErr("");
    if (!form.name.trim()) { setErr("Ad zorunlu"); return; }
    let credentials = {};
    if (form.provider === "PARASUT") {
      if (!form.client_id || !form.client_secret || !form.username || !form.password || !form.company_id) {
        setErr("Tüm Paraşüt alanları zorunlu"); return;
      }
      credentials = {
        client_id: form.client_id, client_secret: form.client_secret,
        username: form.username, password: form.password, company_id: form.company_id,
      };
    } else if (form.provider === "BIRFATURA") {
      if (form.auth_type === "birfatura_outebelgev2") {
        // iter135 — Official OutEBelgeV2 contract
        if (!form.api_key || !form.secret_key || !form.integration_key) {
          setErr("BirFatura OutEBelgeV2 için X-Api-Key, X-Secret-Key ve X-Integration-Key zorunlu"); return;
        }
        credentials = {
          auth_type: "birfatura_outebelgev2",
          api_key: form.api_key,
          secret_key: form.secret_key,
          integration_key: form.integration_key,
          own_tax_ids: form.own_tax_ids.split(",").map(s => s.trim()).filter(Boolean),
        };
        if (form.base_url.trim()) credentials.base_url = form.base_url.trim();} else {
        // Legacy custom endpoint
        if (!form.base_url) { setErr("BirFatura base_url zorunlu"); return; }
        let mapping;
        if (form.mapping_json.trim()) {
          try { mapping = JSON.parse(form.mapping_json); }
          catch { setErr("Mapping JSON geçersiz"); return; }
        }
        credentials = {
          base_url: form.base_url.trim(),
          list_incoming_path: form.list_incoming_path.trim(),
          list_outgoing_path: form.list_outgoing_path.trim(),
          auth_type: form.auth_type,
          auth_header_name: form.auth_header_name || "X-API-Key",
          auth_token: form.auth_token || undefined,
          api_key: form.api_key || undefined,
          username: form.username || undefined,
          password: form.password || undefined,
          own_tax_ids: form.own_tax_ids.split(",").map(s => s.trim()).filter(Boolean),
          mapping,
        };
      }
    } else {
      credentials = { api_key: form.sopyo_api_key };
    }
    setBusy(true);
    try {
      await api.post("/invoices/connections", {
        provider: form.provider, name: form.name, credentials,
        own_company: form.own_company, active: true,
      });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Hata"); } finally { setBusy(false); }
  };
  const isPar = form.provider === "PARASUT";
  const isBF = form.provider === "BIRFATURA";
  const isSopyo = form.provider === "PASTEL_BULUT";
  return (
    <Modal open={true} onClose={onClose} title="YENİ PROVIDER BAĞLANTISI" size="md">
      <div className="space-y-3" data-testid="fm-conn-modal">
        <Select label="Provider *" value={form.provider} onChange={e => setForm({ ...form, provider: e.target.value })} data-testid="fm-conn-provider">
          <option value="PARASUT">Paraşüt</option>
          <option value="BIRFATURA">BirFatura (EFaturaV2)</option>
          <option value="PASTEL_BULUT">Pastel Bulut</option>
        </Select>
        {isSopyo && (
          <div className="bg-amber-50/70 border border-amber-400 rounded p-2 text-[11px] text-amber-900" data-testid="fm-conn-unavail-warning">
            <b>⚠ Sopyo/Pastel Bulut kamuya açık fatura-listeleme API'sı yok.</b>
            <ul className="list-disc ml-4 mt-1 space-y-0.5">
              <li>Sopyo destek ekibinden özel endpoint alınmalı. Bağlantı kaydedebilirsiniz ancak Senkronize çalışmayacak.</li>
              <li>Bu provider için Manuel Fatura sekmesini kullanın.</li>
            </ul>
          </div>
        )}
        <Input label="Bağlantı Adı *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="ör. CustomerERP - BirFatura" data-testid="fm-conn-name"/>
        <Select label="Bizim Firmamız" value={form.own_company} onChange={e => setForm({ ...form, own_company: e.target.value })} data-testid="fm-conn-own-company">
          {(constants.own_companies || []).map(oc => <option key={oc} value={oc}>{oc}</option>)}
        </Select>
        {isPar && (
          <>
            <div className="text-[11px] text-slate-600 bg-indigo-50/60 border border-indigo-200 rounded p-2" data-testid="fm-conn-parasut-help">
              Paraşüt credentials'ı için Uygulamalar → API bilgileri sayfasından alın. Company ID paneli URL'sinde görünür.
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input label="Client ID *" value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })} data-testid="fm-conn-client-id"/>
              <Input label="Client Secret *" type="password" value={form.client_secret} onChange={e => setForm({ ...form, client_secret: e.target.value })} data-testid="fm-conn-client-secret"/>
              <Input label="Kullanıcı (email) *" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} data-testid="fm-conn-username"/>
              <Input label="Şifre *" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="fm-conn-password"/>
              <Input label="Company ID *" value={form.company_id} onChange={e => setForm({ ...form, company_id: e.target.value })} data-testid="fm-conn-company-id"/>
            </div>
          </>
        )}
        {isBF && (
          <div className="space-y-2" data-testid="fm-conn-birfatura-form">
            <div className="text-[11px] text-slate-600 bg-indigo-50/60 border border-indigo-200 rounded p-2">
              <b>BirFatura Resmi API — OutEBelgeV2 Listeleme</b>. Anahtarları BirFatura paneli → <b>Uygulamalar → API Bilgileri</b> → "Kendim Kullanacağım · EFaturaV2 Belge İşlemleri" ekranından alın. Base URL boş bırakılırsa resmi <code>https://uygulama.edonustur.com</code> kullanılır.
            </div>
            {form.auth_type === "birfatura_outebelgev2" ? (
              <div className="space-y-2">
                <Input label="X-Api-Key *" type="password" value={form.api_key} onChange={e => setForm({ ...form, api_key: e.target.value })} placeholder="BirFatura panelinden alınır" data-testid="fm-conn-bf-api-key"/>
                <Input label="X-Secret-Key *" type="password" value={form.secret_key} onChange={e => setForm({ ...form, secret_key: e.target.value })} placeholder="BirFatura panelinden alınır" data-testid="fm-conn-bf-secret-key"/>
                <Input label="X-Integration-Key *" type="password" value={form.integration_key} onChange={e => setForm({ ...form, integration_key: e.target.value })} placeholder="BirFatura panelinden alınır" data-testid="fm-conn-bf-integration-key"/>
                <div className="grid grid-cols-2 gap-2">
                  <Input label="Bizim VKN'lerimiz (virgülle)" value={form.own_tax_ids} onChange={e => setForm({ ...form, own_tax_ids: e.target.value })} placeholder="1234567890, 9876543210" data-testid="fm-conn-bf-own-vkns"/>
                  <Input label="Base URL (opsiyonel)" value={form.base_url} onChange={e => setForm({ ...form, base_url: e.target.value })} placeholder="https://uygulama.edonustur.com" data-testid="fm-conn-bf-base"/>
                </div>
              </div>
            ) : (
              <div className="space-y-2 border border-amber-300 rounded p-2 bg-amber-50/50">
                <div className="text-[10px] font-semibold uppercase text-amber-800">LEGACY — Özel Endpoint ({form.auth_type})</div>
                <Input label="Base URL *" value={form.base_url} onChange={e => setForm({ ...form, base_url: e.target.value })} data-testid="fm-conn-bf-base"/>
                <div className="grid grid-cols-2 gap-2">
                  <Input label="Gelen Fatura Path" value={form.list_incoming_path} onChange={e => setForm({ ...form, list_incoming_path: e.target.value })} data-testid="fm-conn-bf-incoming"/>
                  <Input label="Giden Fatura Path" value={form.list_outgoing_path} onChange={e => setForm({ ...form, list_outgoing_path: e.target.value })} data-testid="fm-conn-bf-outgoing"/>
                </div>
                {form.auth_type === "bearer" && (
                  <Input label="Bearer Token" type="password" value={form.auth_token} onChange={e => setForm({ ...form, auth_token: e.target.value })} data-testid="fm-conn-bf-token"/>
                )}
                {form.auth_type === "basic" && (
                  <div className="grid grid-cols-2 gap-2">
                    <Input label="Kullanıcı" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })}/>
                    <Input label="Şifre" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}/>
                  </div>
                )}
                {form.auth_type === "api_key" && (
                  <div className="grid grid-cols-2 gap-2">
                    <Input label="Header Adı" value={form.auth_header_name} onChange={e => setForm({ ...form, auth_header_name: e.target.value })}/>
                    <Input label="API Key" type="password" value={form.api_key} onChange={e => setForm({ ...form, api_key: e.target.value })}/>
                  </div>
                )}
                <Input label="Bizim VKN'lerimiz (virgülle)" value={form.own_tax_ids} onChange={e => setForm({ ...form, own_tax_ids: e.target.value })} data-testid="fm-conn-bf-own-vkns"/>
                <Textarea label="Mapping JSON (opsiyonel)" rows={3} value={form.mapping_json} onChange={e => setForm({ ...form, mapping_json: e.target.value })}/>
                <button type="button" onClick={() => setForm({ ...form, auth_type: "birfatura_outebelgev2" })}
                  className="text-[10px] text-indigo-700 hover:underline">Resmi OutEBelgeV2 kontratına geç →</button>
              </div>
            )}
            <div className="text-right">
              <button type="button" onClick={() => setShowAdvanced(v => !v)}
                className="text-[10px] text-slate-400 hover:text-slate-600 hover:underline"
                data-testid="fm-conn-bf-advanced-toggle">
                {showAdvanced ? "Gelişmiş modu kapat" : "Gelişmiş (özel endpoint)"}
              </button>
              {showAdvanced && (
                <div className="mt-1">
                  <Select label="Auth Modu (özel endpoint için)" value={form.auth_type}
                    onChange={e => setForm({ ...form, auth_type: e.target.value })}
                    data-testid="fm-conn-bf-auth-type">
                    <option value="birfatura_outebelgev2">BirFatura OutEBelgeV2 (Resmi - Önerilen)</option>
                    <option value="bearer">Bearer Token (özel endpoint)</option>
                    <option value="basic">HTTP Basic (özel endpoint)</option>
                    <option value="api_key">API Key (custom header, özel endpoint)</option>
                  </Select>
                </div>
              )}
            </div>
          </div>
        )}
        {isSopyo && (
          <Input label="Sopyo API Key" value={form.sopyo_api_key} onChange={e => setForm({ ...form, sopyo_api_key: e.target.value })}/>
        )}
        {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid="fm-conn-err">{err}</div>}
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
          <Btn variant="ghost" onClick={onClose}>İptal</Btn>
          <Btn onClick={submit} disabled={busy} className="bg-emerald-600 text-white" data-testid="fm-conn-save">
            {busy ? "Kaydediliyor..." : "Bağlantıyı Kaydet"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
